/**
 * Start / continue an RN assessment straight from a booked calendar visit
 * (appointments tagged assessment_kind = supervisory | readmission).
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import StartAssessmentDialog from "./StartAssessmentDialog";
import AssessmentFiller from "./AssessmentFiller";
import { ClipboardPen, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const APPT_KIND_LABEL = { supervisory: "RN Supervisory Visit", readmission: "RN Re-Admission" };
export const APPT_KIND_COLOR = {
  supervisory: "bg-rose-100 text-rose-700 border-rose-200",
  readmission: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function AppointmentAssessmentButton({ appointment, onChanged, size = "sm" }) {
  const { user } = useAuth();
  const canEdit = ["rn", "admin"].includes((user?.role || "").toLowerCase());
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const closeFiller = useCallback(() => setOpenId(null), []);

  if (!appointment?.assessment_kind || appointment.status !== "scheduled" || !canEdit) return null;

  const launch = async () => {
    setChecking(true);
    try {
      const r = await axios.get(`${API}/assessments`, { params: { client_id: appointment.client_id, status: "draft", assessment_type: appointment.assessment_kind } });
      const draft = (r.data.assessments || [])[0];
      if (draft) { setOpenId(draft.id); toast.info("Continuing the assessment already in progress"); }
      else setStarting(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open assessment");
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <Button size={size} onClick={launch} disabled={checking} className="gap-1 bg-rose-600 hover:bg-rose-700" data-testid={`appt-start-assessment-${appointment.id}`}>
        {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardPen className="w-3.5 h-3.5" />} Start assessment
      </Button>
      <StartAssessmentDialog
        open={starting}
        onClose={() => setStarting(false)}
        clientId={appointment.client_id}
        clientName={appointment.client_name}
        defaultType={appointment.assessment_kind}
        defaultVisitDate={appointment.scheduled_date}
        onStarted={(a) => setOpenId(a.id)}
      />
      {openId && <AssessmentFiller assessmentId={openId} onClose={closeFiller} onCompleted={onChanged} />}
    </>
  );
}
